import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { IconButton, Tooltip, Button } from '@mui/material'
import { ContentCopy as CopyIcon, Replay as RetryIcon } from '@mui/icons-material'
import { ToolExecution, ProvenanceStep } from '../types'

export interface ToolExecutionCardProps {
    tool: ToolExecution
    index?: number
    totalTools?: number
    expanded?: boolean
    showTimeline?: boolean
    showProvenance?: boolean
    onRetry?: (tool: ToolExecution) => void
}

function formatToolName(name: string): string {
    return String(name || '')
        .replace(/[_.]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
        .trim() || name
}

function getToolIcon(name: string): string {
    const lower = name.toLowerCase()
    if (lower.includes('email')) return '📧'
    if (lower.includes('search')) return '🔍'
    if (lower.includes('create')) return '➕'
    if (lower.includes('update') || lower.includes('set')) return '✏️'
    if (lower.includes('delete') || lower.includes('remove')) return '🗑️'
    if (lower.includes('list') || lower.includes('get')) return '📋'
    if (lower.includes('validate')) return '✅'
    if (lower.includes('send')) return '📤'
    return '🔧'
}

function getStatusIcon(status: ToolExecution['status']): string {
    switch (status) {
        case 'completed': return '✓'
        case 'failed': return '✗'
        case 'executing': return '⏳'
        default: return '○'
    }
}

function getStatusColor(status: ToolExecution['status']): string {
    switch (status) {
        case 'completed': return 'var(--success-color, #22c55e)'
        case 'failed': return 'var(--error-color, #ef4444)'
        case 'executing': return 'var(--main-accent, #3b82f6)'
        default: return 'var(--main-text-tertiary, #94a3b8)'
    }
}

function normalizeToMs(timestamp: number): number {
    return timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp
}

function formatDuration(startTimestamp: number, endTimestamp?: number): string {
    const startMs = normalizeToMs(startTimestamp)
    const endMs = endTimestamp ? normalizeToMs(endTimestamp) : Date.now()
    const durationMs = Math.max(0, endMs - startMs)
    if (durationMs < 1000) return `${Math.round(durationMs)}ms`
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`
    const minutes = Math.floor(durationMs / 60000)
    const seconds = ((durationMs % 60000) / 1000).toFixed(0)
    return `${minutes}m ${seconds}s`
}

function getProvenanceBadge(step: ProvenanceStep['step']): { label: string; color: string } {
    switch (step) {
        case 'user-provided': return { label: 'User', color: 'var(--link-color, #3b82f6)' }
        case 'x-default': return { label: 'Default', color: 'var(--main-text-secondary, #64748b)' }
        case 'x-normalize': return { label: 'Normalized', color: 'var(--warning-color, #f59e0b)' }
        case 'x-infer': return { label: 'Inferred', color: 'var(--main-accent, #8b5cf6)' }
        case 'x-suggest': return { label: 'Suggested', color: 'var(--success-color, #22c55e)' }
        case 'llm-generated': return { label: 'AI', color: 'var(--main-accent, #8b5cf6)' }
        default: return { label: step, color: 'var(--main-text-secondary, #64748b)' }
    }
}

const ProvenanceChain: React.FC<{ steps: ProvenanceStep[] }> = ({ steps }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
        {steps.map((step, i) => {
            const badge = getProvenanceBadge(step.step)
            return (
                <React.Fragment key={i}>
                    <span
                        title={`${step.step}: ${JSON.stringify(step.output)}`}
                        style={{
                            display: 'inline-flex',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 500,
                            background: `color-mix(in srgb, ${badge.color} 15%, transparent)`,
                            color: badge.color,
                            border: `1px solid color-mix(in srgb, ${badge.color} 30%, transparent)`,
                        }}
                    >
                        {badge.label}
                    </span>
                    {i < steps.length - 1 && (
                        <span style={{ color: 'var(--main-text-tertiary, #94a3b8)', fontSize: 10 }}>→</span>
                    )}
                </React.Fragment>
            )
        })}
    </div>
)

export const ToolExecutionCard: React.FC<ToolExecutionCardProps> = ({
    tool,
    index = 0,
    totalTools = 1,
    expanded = false,
    showTimeline = true,
    showProvenance = true,
    onRetry,
}) => {
    const [isExpanded, setIsExpanded] = useState(expanded)
    const [showParams, setShowParams] = useState(false)
    const [showResult, setShowResult] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const [elapsedTime, setElapsedTime] = useState('')

    const [copied, setCopied] = useState(false)

    const handleCopyResult = useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!tool.result) return
        const text =
            typeof tool.result.result === 'string'
                ? tool.result.result
                : JSON.stringify(tool.result.result, null, 2)
        try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            /* clipboard unavailable */
        }
    }, [tool.result])

    useEffect(() => { setIsExpanded(expanded) }, [expanded])

    // Staggered entrance animation
    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), index * 80)
        return () => clearTimeout(timer)
    }, [index])

    // Live elapsed time for executing tools
    useEffect(() => {
        if (tool.status === 'executing') {
            const interval = setInterval(() => {
                setElapsedTime(formatDuration(tool.timestamp))
            }, 100)
            return () => clearInterval(interval)
        } else if (tool.status === 'completed' || tool.status === 'failed') {
            setElapsedTime(formatDuration(tool.timestamp, tool.endTimestamp || Date.now()))
        }
    }, [tool.status, tool.timestamp, tool.endTimestamp])

    const statusColor = getStatusColor(tool.status)
    const isLast = index === totalTools - 1

    const receipt = tool.result?.receipt
    const correlationId = receipt?.execution?.correlation_id
    const provenanceSteps = useMemo(() => {
        const prov = receipt?.parameters?.provenance
        if (!prov) return [] as ProvenanceStep[]
        return Object.values(prov).flat()
    }, [receipt])

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '10px 12px 10px 16px',
                borderRadius: 8,
                background: 'var(--card-bg, #fff)',
                border: `1px solid var(--card-border, #e2e8f0)`,
                borderLeft: `3px solid ${statusColor}`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.25s ease-out, transform 0.25s ease-out',
                position: 'relative',
                marginBottom: showTimeline && !isLast ? 8 : 0,
            }}
        >
            {/* Vertical timeline connector */}
            {showTimeline && !isLast && (
                <div style={{
                    position: 'absolute',
                    left: 6,
                    top: '100%',
                    width: 2,
                    height: 8,
                    background: `linear-gradient(to bottom, ${statusColor}, var(--card-border, #e2e8f0))`,
                }} />
            )}
            {/* Timeline dot */}
            {showTimeline && (
                <div style={{
                    position: 'absolute',
                    left: 4,
                    top: 14,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: statusColor,
                    boxShadow: tool.status === 'executing' ? `0 0 6px ${statusColor}` : 'none',
                }} />
            )}

            {/* Header row */}
            <button
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    background: 'none', border: 'none', padding: 0, width: '100%',
                    textAlign: 'left', font: 'inherit',
                }}
                aria-expanded={isExpanded}
                aria-label={`Toggle tool details for ${formatToolName(tool.name)}`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span style={{ fontSize: 14 }} aria-hidden="true">{getToolIcon(tool.name)}</span>
                <span style={{ flex: 1, fontWeight: 500, fontSize: 12, color: 'var(--main-text, #1e293b)' }}>
                    {formatToolName(tool.name)}
                </span>
                {elapsedTime && (
                    <span style={{
                        padding: '1px 5px',
                        borderRadius: 4,
                        fontSize: 10,
                        fontFamily: 'monospace',
                        background: 'var(--main-bg, #f0f4f8)',
                        color: 'var(--main-text-secondary, #64748b)',
                    }}>
                        ⏱ {elapsedTime}
                    </span>
                )}
                <span
                    aria-hidden="true"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: `color-mix(in srgb, ${statusColor} 18%, transparent)`,
                        color: statusColor,
                        fontSize: 11,
                        fontWeight: 700,
                    }}>
                    {getStatusIcon(tool.status)}
                </span>
                <span aria-hidden="true" style={{
                    color: 'var(--main-text-tertiary, #94a3b8)',
                    fontSize: 10,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                }}>▼</span>
            </button>

            {/* Expanded content */}
            {isExpanded && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    paddingTop: 8,
                    borderTop: '1px solid var(--card-border, #e2e8f0)',
                }}>
                    {/* Parameters */}
                    <div>
                        <button
                            onClick={e => { e.stopPropagation(); setShowParams(!showParams) }}
                            aria-expanded={showParams}
                            aria-label="Toggle tool parameters"
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                background: 'none', border: 'none',
                                color: 'var(--link-color, #3b82f6)',
                                cursor: 'pointer', fontSize: 11, padding: 0,
                            }}
                        >
                            {showParams ? '▼' : '▶'} Parameters ({Object.keys(tool.arguments).length})
                        </button>
                        {showParams && (
                            <div style={{
                                marginTop: 6,
                                padding: 8,
                                borderRadius: 6,
                                background: 'var(--main-bg, #f0f4f8)',
                                fontSize: 11,
                                fontFamily: 'monospace',
                            }}>
                                {Object.entries(tool.arguments).map(([key, value]) => (
                                    <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                                        <span style={{ color: 'var(--main-accent, #3b82f6)', minWidth: 90, flexShrink: 0 }}>{key}:</span>
                                        <span style={{ color: 'var(--main-text, #1e293b)', wordBreak: 'break-all' }}>
                                            {typeof value === 'string' ? value : JSON.stringify(value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Result */}
                    {tool.status === 'completed' && tool.result && (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                    onClick={e => { e.stopPropagation(); setShowResult(!showResult) }}
                                    aria-expanded={showResult}
                                    aria-label="Toggle tool result"
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 4,
                                        background: 'none', border: 'none',
                                        color: 'var(--link-color, #3b82f6)',
                                        cursor: 'pointer', fontSize: 11, padding: 0,
                                    }}
                                >
                                    {showResult ? '▼' : '▶'} Result
                                </button>
                                <Tooltip title={copied ? 'Copied' : 'Copy result'} slotProps={{ popper: { style: { zIndex: 100000 } } }}>
                                    <IconButton
                                        size="small"
                                        aria-label="Copy tool result"
                                        onClick={handleCopyResult}
                                        sx={{ p: 0.25, color: 'var(--main-text-secondary, #64748b)' }}
                                    >
                                        <CopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                            {showResult && (
                                <div style={{
                                    marginTop: 6,
                                    padding: 8,
                                    borderRadius: 6,
                                    background: 'var(--main-bg, #f0f4f8)',
                                    fontSize: 11,
                                    fontFamily: 'monospace',
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                }}>
                                    <pre style={{
                                        margin: 0, whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        color: 'var(--main-text, #1e293b)',
                                    }}>
                                        {typeof tool.result.result === 'string'
                                            ? tool.result.result
                                            : JSON.stringify(tool.result.result, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Error + retry */}
                    {tool.status === 'failed' && tool.error && (
                        <div style={{
                            padding: '6px 8px',
                            borderRadius: 6,
                            background: 'var(--error-bg, #fee2e2)',
                            border: '1px solid var(--error-color, #fecaca)',
                            fontSize: 11,
                            color: 'var(--error-color, #991b1b)',
                        }}>
                            {tool.error}
                            {onRetry && (
                                <Button
                                    size="small"
                                    startIcon={<RetryIcon sx={{ fontSize: 14 }} />}
                                    onClick={e => { e.stopPropagation(); onRetry(tool) }}
                                    sx={{ mt: 0.5, fontSize: 11, textTransform: 'none' }}
                                >
                                    Retry tool
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Provenance chain */}
                    {showProvenance && provenanceSteps.length > 0 && (
                        <div>
                            <div style={{ fontSize: 10, color: 'var(--main-text-secondary, #64748b)', marginBottom: 4 }}>
                                Provenance
                            </div>
                            <ProvenanceChain steps={provenanceSteps} />
                        </div>
                    )}

                    {/* Correlation ID for audit */}
                    {correlationId && (
                        <div style={{
                            fontSize: 10,
                            fontFamily: 'monospace',
                            color: 'var(--main-text-tertiary, #94a3b8)',
                        }}>
                            Correlation: {correlationId.slice(0, 12)}…
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// Inject CSS keyframes once
const toolCardStyleId = 'eid-tool-card-styles'
if (typeof document !== 'undefined' && !document.getElementById(toolCardStyleId)) {
    const style = document.createElement('style')
    style.id = toolCardStyleId
    style.textContent = `
        @keyframes eid-tool-fadein { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes eid-tool-pulse { 0%,100% { opacity:1; } 50% { opacity:0.6; } }
    `
    document.head.appendChild(style)
}

export default ToolExecutionCard
